import type { Rng, RngState } from '../contracts';

/** splitmix32: expands a seed into well-mixed 32-bit words. */
function splitmix32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x9e3779b9) >>> 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b) >>> 0;
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35) >>> 0;
    return (z ^ (z >>> 16)) >>> 0;
  };
}

/** Initial sfc32 state for a seed. */
export function seedRngState(seed: number): RngState {
  const sm = splitmix32(seed);
  const st: RngState = [sm(), sm(), sm(), sm()];
  return st;
}

/**
 * sfc32 PRNG whose state lives in an external holder (e.g. `world.rngState`), so
 * snapshot/restore of the World captures the RNG exactly.
 */
export function createRng(holder: { rngState: RngState }): Rng {
  const next = (): number => {
    const s = holder.rngState;
    let a = s[0] >>> 0, b = s[1] >>> 0, c = s[2] >>> 0, d = s[3] >>> 0;
    const t = (((a + b) >>> 0) + d) >>> 0;
    d = (d + 1) >>> 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) >>> 0;
    c = ((c << 21) | (c >>> 11)) >>> 0;
    c = (c + t) >>> 0;
    holder.rngState = [a >>> 0, b >>> 0, c, d];
    return t / 4294967296;
  };
  return {
    next,
    int: (min, max) => min + Math.floor(next() * (max - min + 1)),
    range: (min, max) => min + next() * (max - min),
    chance: (p) => (p >= 1 ? (next(), true) : next() < p),
    pick: <T>(arr: readonly T[]): T | undefined => (arr.length === 0 ? undefined : arr[Math.floor(next() * arr.length)]),
    getState: () => [...holder.rngState] as RngState,
    setState: (s) => {
      holder.rngState = [...s] as RngState;
    },
  };
}

/** Standalone seeded RNG (bots, harness, content generation). */
export function createSeededRng(seed: number): Rng {
  return createRng({ rngState: seedRngState(seed) });
}
